import { Action, createReducer, on } from "@ngrx/store";

import { ComplianceRegimeActions } from "../../actions/compliance-regime.actions";
import { ComplianceRegimesState } from "../../models/mp-admin/compliance-regimes-state";

const initialState: ComplianceRegimesState = {
	regimes: {},
	vendorRestrictions: {},
	regimeVendors: {},
	regimeVendorsLoaded: {},
	loading: false
};

const reducer = createReducer(
	initialState,

	on(ComplianceRegimeActions.regimes.get, (state) => ({ ...state, loading: true })),
	on(ComplianceRegimeActions.regimes.getApi, (state) => ({ ...state, loading: true })),
	on(ComplianceRegimeActions.regimes.getSuccess, (state, { regimes }) => ({
		...state,
		regimes: regimes.reduce<Record<string, typeof regimes[number]>>((acc, r) => {
			acc[r.code] = r;
			return acc;
		}, {}),
		hasLoaded: true,
		loading: false,
		error: undefined
	})),
	on(ComplianceRegimeActions.regimes.getFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),
	on(ComplianceRegimeActions.regimes.invalidate, (state) => ({
		...state,
		hasLoaded: false
	})),

	on(ComplianceRegimeActions.regimes.createSuccess, (state, { regime }) => ({
		...state,
		regimes: { ...state.regimes, [regime.code]: regime },
		loading: false,
		error: undefined
	})),
	on(ComplianceRegimeActions.regimes.createFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	on(ComplianceRegimeActions.regimes.updateSuccess, (state, { regime }) => {
		const existing = state.regimes[regime.code];
		return {
			...state,
			regimes: {
				...state.regimes,
				[regime.code]: { ...existing, ...regime }
			},
			loading: false,
			error: undefined
		};
	}),
	on(ComplianceRegimeActions.regimes.updateFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	on(ComplianceRegimeActions.regimes.disableSuccess, (state, { code }) => {
		const existing = state.regimes[code];
		if (!existing) {
			return state;
		}
		return {
			...state,
			regimes: {
				...state.regimes,
				[code]: { ...existing, disabled: true }
			},
			loading: false,
			error: undefined
		};
	}),
	on(ComplianceRegimeActions.regimes.enableSuccess, (state, { code }) => {
		const existing = state.regimes[code];
		if (!existing) {
			return state;
		}
		return {
			...state,
			regimes: {
				...state.regimes,
				[code]: { ...existing, disabled: false }
			},
			loading: false,
			error: undefined
		};
	}),
	on(
		ComplianceRegimeActions.regimes.disableFail,
		ComplianceRegimeActions.regimes.enableFail,
		ComplianceRegimeActions.regimes.mapProductsFail,
		(state, { error }) => ({ ...state, loading: false, error })
	),

	on(
		ComplianceRegimeActions.regimes.mapProductsSuccess,
		(state, { code, productIds }) => {
			const existing = state.regimes[code];
			if (!existing) {
				return state;
			}
			return {
				...state,
				regimes: {
					...state.regimes,
					[code]: { ...existing, restrictedProductIds: productIds }
				},
				loading: false,
				error: undefined
			};
		}
	),

	on(ComplianceRegimeActions.vendorRegimes.getSuccess, (state, { vendorId, restrictions }) => ({
		...state,
		vendorRestrictions: {
			...state.vendorRestrictions,
			[vendorId]: restrictions
		},
		loading: false,
		error: undefined
	})),
	on(
		ComplianceRegimeActions.vendorRegimes.getFail,
		ComplianceRegimeActions.vendorRegimes.assignFail,
		ComplianceRegimeActions.vendorRegimes.removeFail,
		(state, { error }) => ({ ...state, loading: false, error })
	),

	on(ComplianceRegimeActions.vendorRegimes.assignSuccess, (state, { vendorId, restriction }) => {
		const existing = state.vendorRestrictions[vendorId] || [];
		const filtered = existing.filter((r) => r.regimeCode !== restriction.regimeCode);
		return {
			...state,
			vendorRestrictions: {
				...state.vendorRestrictions,
				[vendorId]: [...filtered, restriction]
			},
			loading: false,
			error: undefined
		};
	}),

	on(ComplianceRegimeActions.vendorRegimes.removeSuccess, (state, { vendorId, regimeCode }) => {
		const existing = state.vendorRestrictions[vendorId] || [];
		return {
			...state,
			vendorRestrictions: {
				...state.vendorRestrictions,
				[vendorId]: existing.filter((r) => r.regimeCode !== regimeCode)
			},
			loading: false,
			error: undefined
		};
	}),

	on(
		ComplianceRegimeActions.regimes.vendors.getSuccess,
		(state, { code, vendors }) => ({
			...state,
			regimeVendors: { ...state.regimeVendors, [code]: vendors },
			regimeVendorsLoaded: { ...state.regimeVendorsLoaded, [code]: true },
			loading: false,
			error: undefined
		})
	),
	on(
		ComplianceRegimeActions.regimes.vendors.invalidate,
		(state, { code }) => ({
			...state,
			regimeVendorsLoaded: { ...state.regimeVendorsLoaded, [code]: false }
		})
	),
	on(
		ComplianceRegimeActions.regimes.vendors.replaceSuccess,
		(state, { code }) => ({
			...state,
			regimeVendorsLoaded: { ...state.regimeVendorsLoaded, [code]: false },
			loading: false,
			error: undefined
		})
	),
	on(
		ComplianceRegimeActions.regimes.vendors.getFail,
		ComplianceRegimeActions.regimes.vendors.replaceFail,
		(state, { error }) => ({ ...state, loading: false, error })
	),

	on(ComplianceRegimeActions.regimeMappings.getSuccess, (state, { mappings }) => ({
		...state,
		mappings,
		mappingsLoaded: true,
		loading: false,
		error: undefined
	})),
	on(ComplianceRegimeActions.regimeMappings.getFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),
	on(ComplianceRegimeActions.regimeMappings.invalidate, (state) => ({
		...state,
		mappingsLoaded: false
	}))
);

export function complianceRegimesReducer(
	state: ComplianceRegimesState | undefined,
	action: Action
): ComplianceRegimesState {
	return reducer(state, action);
}
